import {
  createRouter,
  createRootRoute,
  createRoute,
  lazyRouteComponent,
  notFound,
  Outlet,
} from "@tanstack/react-router";
import { isMultiLocale, SUPPORTED_LOCALES } from "../translations/config";

// Import components
import { RootLayout } from "./routes/__root";
import { LocaleLayout } from "./routes/LocaleLayout";
import { LandingPage } from "./pages/landing/LandingPage";
import { LoginPage } from "./pages/auth/login/LoginPage";
import { VerifyLoginPage } from "./pages/auth/login/VerifyLoginPage";
import { SignupPage } from "./pages/auth/signup/SignupPage";
import { CheckEmailPage } from "./pages/auth/signup/CheckEmailPage";
import { ForgotPasswordPage } from "./pages/auth/forgot-password/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/auth/reset-password/ResetPasswordPage";
import { ProtectedRoute } from "./components/auth";
import { DashboardLayout } from "./components/dashboard";
import { NotFoundPage, ServerErrorPage } from "./pages/error";
import { ExtensionPage } from "./pages/extension/ExtensionPage";
import { GuidePage } from "./pages/guides/GuidePage";
import { GUIDE_PAGES } from "./config/guides";
import type { InboxSearch, MessageFilter } from "./types/mail";

// Signed-in pages load on demand, so public pages don't download the app,
// the admin dashboard (zod, dnd-kit) or the mail renderer (dompurify).
const ProfilePage = lazyRouteComponent(() => import("./pages/profile/ProfilePage"), "ProfilePage");
const DashboardContent = lazyRouteComponent(
  () => import("./pages/dashboard/components/DashboardContent"),
  "DashboardContent",
);
const ProjectsPage = lazyRouteComponent(
  () => import("./pages/dashboard/ProjectsPage"),
  "ProjectsPage",
);
const SettingsPage = lazyRouteComponent(
  () => import("./pages/dashboard/SettingsPage"),
  "SettingsPage",
);
const BillingPage = lazyRouteComponent(
  () => import("./pages/dashboard/BillingPage"),
  "BillingPage",
);
const RoadmapPage = lazyRouteComponent(
  () => import("./pages/dashboard/RoadmapPage"),
  "RoadmapPage",
);
const AppLayout = lazyRouteComponent(() => import("./pages/app/AppLayout"), "AppLayout");
const InboxPage = lazyRouteComponent(() => import("./pages/app/InboxPage"), "InboxPage");
const DomainsPage = lazyRouteComponent(() => import("./pages/app/DomainsPage"), "DomainsPage");
const DomainDetailPage = lazyRouteComponent(
  () => import("./pages/app/DomainDetailPage"),
  "DomainDetailPage",
);
const AppSettingsPage = lazyRouteComponent(
  () => import("./pages/app/SettingsPage"),
  "SettingsPage",
);
const LeaksPage = lazyRouteComponent(() => import("./pages/app/LeaksPage"), "LeaksPage");
const LeakDetailPage = lazyRouteComponent(
  () => import("./pages/app/LeakDetailPage"),
  "LeakDetailPage",
);

/**
 * Dashboard Layout Wrapper
 *
 * Wraps dashboard routes with ProtectedRoute and DashboardLayout.
 */
function DashboardLayoutWrapper() {
  return (
    <ProtectedRoute>
      <DashboardLayout>
        <Outlet />
      </DashboardLayout>
    </ProtectedRoute>
  );
}

/**
 * Route Tree
 *
 * Define the application's route hierarchy using TanStack Router.
 * When isMultiLocale is true, public pages are also accessible under /$locale/.
 */

// Root route with layout and error handlers
const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFoundPage,
  errorComponent: ServerErrorPage,
});

// Index/Landing page
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: LandingPage,
});

const str = (v: unknown) => (typeof v === "string" && v.length > 0 ? v : undefined);

/** `?email=` on the signup pages: the address to prefill / the one the mail went to. */
const emailSearch = (search: Record<string, unknown>): { email?: string } => ({
  email: str(search.email),
});

// Auth routes
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const verifyLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login/verify",
  component: VerifyLoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignupPage,
  validateSearch: emailSearch,
});

const checkEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup/check-email",
  component: CheckEmailPage,
  validateSearch: emailSearch,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPasswordPage,
});

const extensionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/extension",
  component: ExtensionPage,
});

// SEO landing pages, one route per entry in GUIDE_PAGES
const guidePageRoutes = Object.entries(GUIDE_PAGES).map(([path, key]) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path,
    component: () => <GuidePage guideKey={key} />,
  }),
);

// Protected routes
const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/profile",
  component: ProfilePage,
});

// Dashboard layout route (parent for all dashboard pages)
const dashboardLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  component: DashboardLayoutWrapper,
});

// Dashboard index route (main dashboard page)
const dashboardIndexRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/",
  component: DashboardContent,
});

// Dashboard sub-routes (internal admin pages)
const projectsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/projects",
  component: ProjectsPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/settings",
  component: SettingsPage,
});

const billingRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/billing",
  component: BillingPage,
});

const roadmapRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/roadmap",
  component: RoadmapPage,
});

// Mailfinity app (/app): inbox, domains, settings
const FILTERS: MessageFilter[] = ["all", "unread", "attachments", "unfamiliar"];
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app",
  component: AppLayout,
});

const appInboxRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/",
  component: InboxPage,
  validateSearch: (search: Record<string, unknown>): InboxSearch => ({
    domain: str(search.domain),
    address: str(search.address),
    message: str(search.message),
    q: str(search.q),
    from: str(search.from),
    filter: FILTERS.includes(search.filter as MessageFilter)
      ? (search.filter as MessageFilter)
      : undefined,
  }),
});

const appDomainsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/domains",
});

const appDomainsIndexRoute = createRoute({
  getParentRoute: () => appDomainsRoute,
  path: "/",
  component: DomainsPage,
  validateSearch: (search: Record<string, unknown>): { add?: boolean } => ({
    add: search.add === true || search.add === "true" ? true : undefined,
  }),
});

const appDomainDetailRoute = createRoute({
  getParentRoute: () => appDomainsRoute,
  path: "/$domainId",
  component: DomainDetailPage,
});

const appSettingsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/settings",
  component: AppSettingsPage,
  validateSearch: (search: Record<string, unknown>): { tab?: string } => ({
    tab: str(search.tab),
  }),
});

const appLeaksRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/leaks",
});

const appLeaksIndexRoute = createRoute({
  getParentRoute: () => appLeaksRoute,
  path: "/",
  component: LeaksPage,
});

const appLeakDetailRoute = createRoute({
  getParentRoute: () => appLeaksRoute,
  path: "/$company",
  component: LeakDetailPage,
});

const appRouteTree = appLayoutRoute.addChildren([
  appInboxRoute,
  appDomainsRoute.addChildren([appDomainsIndexRoute, appDomainDetailRoute]),
  appLeaksRoute.addChildren([appLeaksIndexRoute, appLeakDetailRoute]),
  appSettingsRoute,
]);

// Public page routes (non-dashboard)
const publicRoutes = [
  indexRoute,
  loginRoute,
  verifyLoginRoute,
  signupRoute,
  checkEmailRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  extensionRoute,
  ...guidePageRoutes,
  profileRoute,
];

// Dashboard route tree
const dashboardRouteTree = dashboardLayoutRoute.addChildren([
  dashboardIndexRoute,
  projectsRoute,
  settingsRoute,
  billingRoute,
  roadmapRoute,
]);

/**
 * Build the route tree.
 * When multi-locale is enabled, add a /$locale layout route wrapping
 * duplicate public page routes so e.g. /nl/ and /nl/login work.
 */
function buildRouteTree() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const children: any[] = [...publicRoutes, appRouteTree, dashboardRouteTree];

  if (isMultiLocale) {
    // Create locale layout route at /$locale.
    // `$locale` matches any single segment, so without this guard /anything
    // would render the landing page. The server already answers 404 for an
    // unsupported locale (see src/server/known-routes.ts) — this makes the
    // rendered page agree with that status instead of being a soft 404.
    const localeLayoutRoute = createRoute({
      getParentRoute: () => rootRoute,
      path: "/$locale",
      beforeLoad: ({ params }) => {
        const { locale } = params as { locale?: string };
        if (!locale || !(SUPPORTED_LOCALES as readonly string[]).includes(locale)) {
          throw notFound();
        }
      },
      component: LocaleLayout,
    });

    // Create locale-prefixed copies of public routes
    const localeIndex = createRoute({
      getParentRoute: () => localeLayoutRoute,
      path: "/",
      component: LandingPage,
    });

    const localeLogin = createRoute({
      getParentRoute: () => localeLayoutRoute,
      path: "/login",
      component: LoginPage,
    });

    const localeVerifyLogin = createRoute({
      getParentRoute: () => localeLayoutRoute,
      path: "/login/verify",
      component: VerifyLoginPage,
    });

    const localeSignup = createRoute({
      getParentRoute: () => localeLayoutRoute,
      path: "/signup",
      component: SignupPage,
      validateSearch: emailSearch,
    });

    const localeCheckEmail = createRoute({
      getParentRoute: () => localeLayoutRoute,
      path: "/signup/check-email",
      component: CheckEmailPage,
      validateSearch: emailSearch,
    });

    const localeForgotPassword = createRoute({
      getParentRoute: () => localeLayoutRoute,
      path: "/forgot-password",
      component: ForgotPasswordPage,
    });

    const localeResetPassword = createRoute({
      getParentRoute: () => localeLayoutRoute,
      path: "/reset-password",
      component: ResetPasswordPage,
    });

    const localeExtension = createRoute({
      getParentRoute: () => localeLayoutRoute,
      path: "/extension",
      component: ExtensionPage,
    });

    children.push(
      localeLayoutRoute.addChildren([
        localeIndex,
        localeLogin,
        localeVerifyLogin,
        localeSignup,
        localeCheckEmail,
        localeForgotPassword,
        localeResetPassword,
        localeExtension,
        ...Object.entries(GUIDE_PAGES).map(([path, key]) =>
          createRoute({
            getParentRoute: () => localeLayoutRoute,
            path,
            component: () => <GuidePage guideKey={key} />,
          }),
        ),
      ]),
    );
  }

  return rootRoute.addChildren(children);
}

const routeTree = buildRouteTree();

/**
 * Router Instance
 *
 * The main router for the application.
 * Export this to use in App.tsx with RouterProvider.
 */
export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  // New pages start at the top; back/forward restores the previous position.
  scrollRestoration: true,
  defaultNotFoundComponent: NotFoundPage,
  defaultErrorComponent: ServerErrorPage,
});

// Type declaration for TypeScript
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export default router;
